import React, { useState } from 'react';
import { PUSH_SUPPORT } from '../../../Services/PushNotifications';
import { driverPushApi } from '../../../Services/PortalApi';
import usePushSetting from '../usePushSetting';
import { PUSH_COPY } from '../portalFormat';
import { BellIcon, BellOffIcon } from './Icons';

/**
 * Phone notifications — the one card, wherever anybody is asked about them.
 *
 * THREE PLACES, ONE COMPONENT
 * ---------------------------
 *   `variant="enrol"`    the moment after "Készen vagy": an invitation, with
 *                        a reason to say yes, and nothing to turn off yet.
 *   `variant="ask"`      the ONE-TIME ask on a screen somebody is already
 *                        using — a driver's day, the dispatch board. Shown
 *                        only to somebody who has not decided, dismissible,
 *                        and it says where the switch lives afterwards, so
 *                        saying "not now" is not saying "never".
 *   `variant="settings"` the permanent switch: in the driver's own menu, and
 *                        in the dispatcher's device sheet. The plain
 *                        off-switch the office asked for.
 *
 * TWO LANGUAGES, ONE COMPONENT. `copy` carries every word: the driver's
 * Hungarian by default (portalFormat's PUSH_COPY), the office's English on
 * the dispatch board (operatorFormat's PUSH_COPY_EN). `api` carries where
 * the subscription is registered — the driver's portal endpoint by default,
 * the office's `/notifications/push` for staff — and nothing else differs.
 *
 * NEVER A DEAD TOGGLE
 * -------------------
 * Every branch below is a DIFFERENT, TRUE sentence. The one thing this must
 * never do is offer a switch that flips and does nothing — which is exactly
 * what would happen on an iPhone in Safari, where Web Push simply does not
 * exist until the site is on the Home Screen. So that case gets its own
 * explanation and no button at all, as does a permission the person has
 * already refused (the browser will not ask twice; only they can undo it in
 * Settings) and a deploy whose server has no keys.
 *
 * THE ONE-TIME ASK NEVER EXPLAINS. `ask` shows a button or nothing: it is an
 * invitation nobody requested, so on a phone that cannot deliver it is
 * silent rather than lecturing. On an iPhone still in Safari that is also
 * what keeps the portal from nagging twice for one act — the honest next
 * step there is to install, which the Home Screen offer already makes, and
 * this card appears only once that has actually worked.
 */
export default function PushSetting({
  variant = 'settings',
  api = driverPushApi,
  copy = PUSH_COPY,
  /** `ask` only: waving the invitation away, for good, on this device. */
  onDismiss = null,
}) {
  const {
    support, loading, busy, isOn, canAsk, enable, disable,
  } = usePushSetting(api);
  // Did THIS visit turn them on? The one-time ask confirms what just
  // happened and then gets out of the way; it must not become a banner
  // saying "notifications are on" on every screen, for ever.
  const [justEnabled, setJustEnabled] = useState(false);

  if (loading) return null;

  // Nothing to turn off, and nothing to invite them to: say why, once, and
  // never show a control that would do nothing.
  if (!isOn && !canAsk) {
    const why = copy.unavailable[support];

    // The one-time ask is silent here; see the note above.
    if (variant === 'ask') return null;
    // On the enrolment screen an explanation nobody asked for is noise —
    // except the iPhone one, which is the whole reason that screen exists.
    if (variant === 'enrol' && support !== PUSH_SUPPORT.IOS_NEEDS_HOME_SCREEN) return null;
    if (!why) return null;

    return (
      <div className="pp-a2hs">
        <div className="pp-a2hs__head">
          <BellOffIcon className="pp-a2hs__icon" size={20} />
          <span>{copy.head}</span>
        </div>
        <div className="pp-a2hs__body">{why}</div>
      </div>
    );
  }

  if (isOn) {
    // The one-time ask has nothing to say to someone who already said yes
    // on an earlier visit: it is an invitation, and the invitation has been
    // accepted. It confirms only the tap that just happened, then goes.
    if (variant === 'ask' && !justEnabled) return null;

    // On an invitation — at enrolment or the one-time ask — confirm and
    // stop: there is nothing left to do, and no switch was asked for.
    if (variant !== 'settings') {
      return (
        <div className="pp-a2hs">
          <div className="pp-a2hs__head">
            <BellIcon className="pp-a2hs__icon" size={20} />
            <span>{copy.onHead}</span>
          </div>
          <div className="pp-a2hs__body">{copy.what}</div>
        </div>
      );
    }

    return (
      <div className="pp-a2hs">
        <div className="pp-a2hs__head">
          <BellIcon className="pp-a2hs__icon" size={20} />
          <span>{copy.head}</span>
        </div>
        <div className="pp-a2hs__body">{copy.onBody(copy.what)}</div>
        <button
          type="button"
          className="pp-btn pp-btn--outline pp-btn--block pp-gap-top"
          disabled={busy}
          onClick={disable}
        >
          {busy ? copy.disabling : copy.disable}
        </button>
      </div>
    );
  }

  // Off, and we may ask. The button IS the user gesture the browser needs —
  // the permission prompt cannot be raised any other way.
  return (
    <div className="pp-a2hs">
      <div className="pp-a2hs__head">
        <BellIcon className="pp-a2hs__icon" size={20} />
        <span>{variant === 'settings' ? copy.head : copy.invite}</span>
      </div>
      <div className="pp-a2hs__body">{copy.what}</div>
      <button
        type="button"
        className={`pp-btn pp-btn--block pp-gap-top ${variant === 'enrol' ? 'pp-btn--outline' : 'pp-btn--primary'}`}
        disabled={busy}
        onClick={async () => {
          await enable();
          setJustEnabled(true);
        }}
      >
        {busy ? copy.enabling : copy.enable}
      </button>
      {/* "Not now" is not "never": the card says where the switch lives, so
          waving it away costs nobody their notifications. */}
      {variant === 'ask' && onDismiss ? (
        <>
          <button
            type="button"
            className="pp-btn pp-btn--quiet pp-btn--block"
            disabled={busy}
            onClick={onDismiss}
          >
            {copy.later}
          </button>
          <div className="pp-a2hs__hint">{copy.laterHint}</div>
        </>
      ) : null}
    </div>
  );
}
